import { useCallback, useEffect, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Auto-refresh helper for tables that should update in real time.
 *
 *   const { lastRefreshed, refreshNow } = useAutoRefresh(load, {
 *     intervalMs: 60_000,          // polling fallback (default 60s)
 *     sseEvents: ["coverage_change", "shift_update"],
 *   });
 *
 * Calls `onRefresh()` on every interval tick and on any SSE event whose `type`
 * matches one of `sseEvents`. Returns `lastRefreshed` (Date) for a "last updated"
 * hint AND `refreshNow` — a stable async fn the caller can bind to a manual
 * Refresh button so the timestamp updates on click too.
 */
export function useAutoRefresh(onRefresh, opts = {}) {
  const { intervalMs = 60_000, sseEvents = [] } = opts;
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh; // keep the latest closure without re-subscribing SSE

  const refreshNow = useCallback(async () => {
    try { await refresh.current(); setLastRefreshed(new Date()); }
    catch { /* handled by caller */ }
  }, []);

  // Stable dep for the SSE effect below — hashing the list means passing
  // a fresh array literal doesn't spin up a new EventSource.
  const sseKey = sseEvents.join("|");

  useEffect(() => {
    const iv = setInterval(() => { refreshNow(); }, intervalMs);
    return () => clearInterval(iv);
  }, [intervalMs, refreshNow]);

  useEffect(() => {
    if (!sseEvents.length || !API_BASE || typeof window === "undefined" || !window.EventSource) return undefined;
    const es = new EventSource(`${API_BASE}/api/notifications/stream`, { withCredentials: true });
    const handler = (ev) => {
      try {
        const data = JSON.parse(ev.data || "{}");
        if (!sseEvents.includes(data.type)) return;
      } catch { return; }
      refreshNow();
    };
    es.addEventListener("notification", handler);
    es.addEventListener("error", () => { /* browser auto-reconnects */ });
    return () => { es.close(); };
  // Depend on the stringified event list so passing a fresh array
  // reference (common when the caller inlines the list) doesn't tear down
  // the SSE connection unless the actual set of events changed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sseKey, refreshNow]);

  return { lastRefreshed, refreshNow };
}
